import { formatAbn } from "utils/abn";
import "layouts/RCTI/agreements.css";
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LuTriangleAlert,
  LuCheck,
  LuBuilding2,
  LuChevronDown,
  LuChevronRight,
  LuTrash2,
  LuUpload,
  LuSearch,
  LuX,
} from "react-icons/lu";
import Cropper from "react-easy-crop";
import { Modal } from "@mui/material";

import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import AppHeader from "components/AppHeader";
import getCroppedImg from "layouts/profile/editMyProfile/cropImage";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { appConstants } from "utils/appConstants";
import {
  addContactData,
  getContactDetailsByID,
  updateContactData,
} from "utils/contactAPI";
import {
  generateDataList,
  getScreenRight,
  showResMsg,
  validateABN,
  validateEmailAddress,
  validatePhoneNumber,
} from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import { searchAbrCompanies, verifyAbrAbn } from "utils/abrAPI";
import { validateQuotationCustomerField, normalizeQuotationCustomerField } from "utils/quotationCustomerValidation";
import useUnsavedChanges from "hooks/useUnsavedChanges";

import "./addContact.css";

const SCREEN_NAME = "Contact";
const contactValidationFields = { companyName: "companyName", name: "contactPerson", emailAddress: "email" };
const contactFieldError = (field, value) =>
  validateQuotationCustomerField(contactValidationFields[field], value)
    .replace(/Company name/g, "Legal name").replace(/company name/g, "legal name");

const CONFIRMATION_OPTIONS = [
  { value: "", label: "Not confirmed" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const toConfirmationValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const fromConfirmationValue = (value) =>
  value === "" ? null : value === "true";

function ContactSelect({ id, value, onChange, options, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => String(option.value) === String(value))
  );
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    setActiveIndex(selectedIndex);
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open, selectedIndex]);

  const choose = (option) => {
    onChange({ target: { value: option.value } });
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        const start = current < 0 ? selectedIndex : current;
        return (start + direction + options.length) % options.length;
      });
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      choose(options[activeIndex < 0 ? selectedIndex : activeIndex]);
    }
  };

  return (
    <div
      className={`contact-theme-select${open ? " is-open" : ""}`}
      ref={rootRef}
    >
      <button
        id={id}
        type="button"
        className="contact-theme-select-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
      >
        <span>{selected?.label || "Select"}</span>
        <LuChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div
          className="contact-theme-select-menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option, index) => {
            const isSelected = String(option.value) === String(value);
            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`contact-theme-select-option${
                  isSelected ? " is-selected" : ""
                }${activeIndex === index ? " is-active" : ""}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(option)}
              >
                <span>{option.label}</span>
                {isSelected && <LuCheck aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AddContact() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const rctiAgreementReturn =
    new URLSearchParams(location.search).get("returnTo") === "/rcti-agreements/new" ||
    location.state?.rctiAgreementReturn === true;
  const expenseSupplier = new URLSearchParams(location.search).get("new") === "expense";
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // App shell details
  const [navOpen, setNavOpen] = useState(false);
  const [abrBusy, setAbrBusy] = useState(false);
  const [abrMatches, setAbrMatches] = useState([]);
  const [abrMessage, setAbrMessage] = useState("");
  const abrRequest = useRef(0);
  useEffect(
    () => () => {
      abrRequest.current += 1;
    },
    []
  );

  const runAbrLookup = async (abn) => {
    const request = ++abrRequest.current;
    setAbrBusy(true);
    setAbrMessage("");
    setAbrMatches([]);
    try {
      if (abn) {
        const business = await verifyAbrAbn(abn);
        if (request !== abrRequest.current) return;
        setAbrMessage("");
        setAbrMatches([{ ...business, verified: true }]);
      } else {
        const { matches } = await searchAbrCompanies(values.companyName);
        if (request !== abrRequest.current) return;
        setAbrMatches(matches);
        setAbrMessage(
          matches.length
            ? "Select a company to check its ABN registration."
            : "No matching companies found."
        );
      }
    } catch (error) {
      if (request === abrRequest.current) setAbrMessage(error.message);
    } finally {
      if (request === abrRequest.current) setAbrBusy(false);
    }
  };

  const applyAbrDetails = (business) => {
    abrRequest.current += 1;
    setValues((current) => ({
      ...current,
      companyName: business.companyName,
      abnNumber: business.abn,
    }));
    setErrors((current) => ({ ...current, companyName: "", abnNumber: "" }));
    setAbrMatches([]);
    setAbrMessage("Company details filled. Review before saving.");
  };
  const loginDetails = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
    } catch {
      return {};
    }
  }, []);
  const organisation =
    loginDetails?.companyName || loginDetails?.name || "Organisation";
  const role = loginDetails?.userRole || "Owner";
  const planName = loginDetails?.planName || "Silver";
  const userName = loginDetails?.name || organisation;
  const profileImage = loginDetails?.profileImage || "";
  const businessUserID = loginDetails?.businessUserID || loginDetails?._id;

  // Country & State data from Redux
  const countryData =
    useSelector((state) => state.countryState?.countryStateMenu?.country) || [];
  const stateData =
    useSelector((state) => state.countryState?.countryStateMenu?.state) || [];
  const stateListRef = useRef([]);
  const countryListRef = useRef([]);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);

  // Form Values
  const [values, setValues] = useState({
    contactType: rctiAgreementReturn || expenseSupplier ? "Supplier" : "Customer",
    contactStatus: "Active",
    companyName: "",
    name: "",
    phoneNumber: "",
    emailAddress: "",
    websiteName: "",
    abnNumber: "",
    gstRegistered: null,
    isLargeBusiness: null,
    isGovernmentEntity: null,
    paymentTerms: "7 Days",
    invoiceReminder: "Yes",
    streetAddress: "",
    address: "",
    addressLine2: "",
    city: "",
    zipCode: "",
    stateId: "",
    countryId: "",
    bankAccountName: "",
    bankBsbCode: "",
    bankAccountNumber: "",
    notes: "",
    contactLogoFileName: "",
    businessUserID: businessUserID,
  });

  // UI state
  const [manualAddressExpanded, setManualAddressExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");

  // Validation Errors
  const [errors, setErrors] = useState({
    companyName: "",
    name: "",
    emailAddress: "",
    phoneNumber: "",
    abnNumber: "",
  });

  // Logo Cropping state
  const [openCropModal, setOpenCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [actualFile, setActualFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const savedSnapshot = useRef({ values: JSON.stringify(values), file: null });
  const unsaved = useUnsavedChanges(
    JSON.stringify(values) !== savedSnapshot.current.values || actualFile !== savedSnapshot.current.file || isSubmitting,
    { documentName: "contact", saveMode: "record", isSaving: isSubmitting, onSave: () => handleSubmit(null, true) }
  );

  // Load Initial Data
  useEffect(() => {
    stateListRef.current = stateData;
    countryListRef.current = countryData;

    generateDataList(countryData, "_id", "countryName").then((res) =>
      setCountryList(res || [])
    );
    generateDataList(stateData, "_id", "stateName").then((res) =>
      setStateList(res || [])
    );

    if (id) {
      dispatch(loadingAction(true));
      getContactDetailsByID(
        { _id: id, businessUserID },
        (res) => {
          dispatch(loadingAction(false));
          const result = res?.Result_Output;
          if (Array.isArray(result) && result.length > 0) {
            const data = result[0];
            setValues((prev) => {
              const loaded = {
              ...prev,
              ...data,
              paymentTerms: data.paymentTerms || "7 Days",
              invoiceReminder: data.invoiceReminder || "Yes",
              contactStatus: data.contactStatus || "Active",
              contactType: data.contactType || "Customer",
              };
              savedSnapshot.current = { values: JSON.stringify(loaded), file: null };
              return loaded;
            });
            if (data.contactLogoFileName) {
              setFileName(data.contactLogoFileName);
              setFileUrl(
                `${appConstants.baseURL}employee/viewFile?fileName=${data.contactLogoFileName}`
              );
            }
            if (data.address || data.city || data.stateId || data.zipCode) {
              setManualAddressExpanded(true);
            }
          }
        },
        dispatch
      );
    }
  }, [id, businessUserID, countryData, stateData, dispatch]);

  // Field change handlers
  const handleInputChange = (field) => (e) => {
    const val = e.target.value;
    if (field === "companyName" || field === "abnNumber") {
      abrRequest.current += 1;
      setAbrBusy(false);
      setAbrMatches([]);
      setAbrMessage("");
    }

    if (field === "abnNumber") {
      let digits = val.replace(/\D/g, "").slice(0, 11);
      let formatted = digits;
      if (digits.length > 2)
        formatted = digits.slice(0, 2) + " " + digits.slice(2);
      if (digits.length > 5)
        formatted =
          digits.slice(0, 2) + " " + digits.slice(2, 5) + " " + digits.slice(5);
      if (digits.length > 8)
        formatted =
          digits.slice(0, 2) +
          " " +
          digits.slice(2, 5) +
          " " +
          digits.slice(5, 8) +
          " " +
          digits.slice(8);
      setValues((prev) => ({ ...prev, [field]: formatted }));
    } else {
      setValues((prev) => ({ ...prev, [field]: val }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleReminderToggle = () => {
    setValues((prev) => {
      const isCurrentlyOn =
        prev.invoiceReminder === "Yes" || prev.invoiceReminder === true;
      return { ...prev, invoiceReminder: isCurrentlyOn ? "No" : "Yes" };
    });
  };

  // Logo file selection and cropping
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size / (1024 * 1024) > 2) {
      showResMsg("File cannot exceed 2 MB", 2000, false);
      return;
    }

    setActualFile(selectedFile);
    setFileName(selectedFile.name);
    setFileUrl(URL.createObjectURL(selectedFile));
    setOpenCropModal(true);
    e.target.value = null;
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const saveCroppedImage = useCallback(async () => {
    try {
      const cropped = await getCroppedImg(fileUrl, croppedAreaPixels);
      setFileUrl(cropped);
      const blob = await fetch(cropped).then((r) => r.blob());
      const newFile = new File([blob], fileName || "logo.png", {
        type: "image/png",
      });
      setActualFile(newFile);
      setOpenCropModal(false);
    } catch (err) {
      console.error(err);
    }
  }, [fileUrl, croppedAreaPixels, fileName]);

  const removeLogo = () => {
    setFileUrl("");
    setActualFile(null);
    setFileName("");
    setValues((prev) => ({ ...prev, contactLogoFileName: "" }));
  };

  // Submit Handler
  const handleSubmit = async (e, leave = false) => {
    e?.preventDefault();
    if (isSubmitting) return false;

    const newErrors = {
      companyName: "",
      name: "",
      emailAddress: "",
      phoneNumber: "",
      abnNumber: "",
    };
    let hasError = false;

    for (const field of Object.keys(contactValidationFields)) {
      newErrors[field] = contactFieldError(field, values[field]);
      if (newErrors[field]) hasError = true;
    }

    const phoneDigits = String(values.phoneNumber || "").replace(/\D/g, "");
    if (
      values.phoneNumber &&
      (phoneDigits.length < 10 || phoneDigits.length > 15)
    ) {
      newErrors.phoneNumber = "Enter a valid mobile number";
      hasError = true;
    }

    if (values.abnNumber && !validateABN(values.abnNumber)) {
      newErrors.abnNumber =
        genericMessage.invalidABN || "Please enter a valid 11-digit ABN";
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) {
      const field = Object.keys(newErrors).find((key) => newErrors[key]);
      document.getElementById(field === "name" ? "primaryName" : field)?.focus();
      return;
    }

    // Build payload
    const formData = new FormData();
    const cleanValues = { ...values };
    for (const [field, validatorField] of Object.entries(contactValidationFields)) {
      cleanValues[field] = normalizeQuotationCustomerField(validatorField, cleanValues[field]);
    }

    // Combine manual address if expanded
    if (manualAddressExpanded && cleanValues.address) {
      const stateObj = stateListRef.current.find(
        (s) => s._id === cleanValues.stateId
      );
      const countryObj = countryListRef.current.find(
        (c) => c._id === cleanValues.countryId
      );
      const combined = [
        cleanValues.address,
        cleanValues.addressLine2,
        cleanValues.city,
        stateObj?.stateName,
        countryObj?.countryName,
        cleanValues.zipCode,
      ]
        .filter(Boolean)
        .join(", ");
      cleanValues.streetAddress = combined;
      cleanValues.state = stateObj?.stateName || "";
      cleanValues.country = countryObj?.countryName || "Australia";
    }

    Object.entries(cleanValues).forEach(([k, v]) => {
      if (v !== undefined && v !== null && k !== "file") {
        if (
          (k === "countryId" || k === "stateId") &&
          (!v || !String(v).trim())
        ) {
          return;
        }
        formData.append(k, v);
      }
    });

    if (actualFile && fileName) {
      formData.append("file", actualFile);
      formData.append("fileName", fileName);
    }

    setIsSubmitting(true);
    const apiCall = id ? updateContactData : addContactData;

    let saved = false;
    try {
    await apiCall(
      formData,
      (res) => {
        if (res?.Result_Status) {
          saved = true;
          savedSnapshot.current = { values: JSON.stringify(values), file: actualFile };
          unsaved.markSaved();
          showResMsg(
            res.Result_Message || (id ? "Contact updated successfully" : "Contact added successfully"),
            2000,
            true
          );
          const savedContactID = res.Result_Output?.contactVerifiedStatus === "Pending" ? "" : id || res.Result_Output?._id;
          if (!leave) navigate(
            rctiAgreementReturn
              ? `/rcti-agreements/new${savedContactID ? `?supplier=${encodeURIComponent(savedContactID)}` : ""}`
              : "/contacts"
          );
        } else {
          const msg = res?.Result_Message || "Failed to save contact";
          const isDuplicate =
            /already exist|duplicate/i.test(msg) ||
            /email.*exist/i.test(msg) ||
            /mobile.*exist/i.test(msg);

          if (isDuplicate) {
            setDuplicateMessage(
              msg ||
                "A contact with a similar name or ABN already exists. Review the existing contact before creating another."
            );
            setDuplicateModalOpen(true);
          } else {
            showResMsg(msg, 2500, false);
          }
        }
      },
      dispatch
    );
    } catch {
      showResMsg("Unable to save contact. Please try again.", 2500, false);
    } finally {
      setIsSubmitting(false);
    }
    return saved;
  };

  const isReminderOn =
    values.invoiceReminder === "Yes" || values.invoiceReminder === true;
  const isEditing = Boolean(id);
  const pageTitle = isEditing ? "Edit contact" : "Add contact";
  const pageSubtitle = isEditing
    ? "Update this customer’s details"
    : "Enter a legal name and email address to get started";

  return (
    <div
      className={`add-contact-page business-dashboard${
        darkMode ? " dark" : ""
      }`}
      data-theme={darkMode ? "dark" : "light"}
    >
      {unsaved.dialog}
      {/* Sidebar Navigation */}
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        organisation={organisation}
        role={role}
        planName={planName}
      />

      <div className="dashboard-main">
        {/* App Header */}
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={userName}
          profileImage={profileImage}
        />

        <div className="add-contact-editor-bar">
          <div className="add-contact-breadcrumb" aria-label="Breadcrumb">
            <Link to="/contacts">Contacts</Link>
            <LuChevronRight aria-hidden="true" />
            <span>{pageTitle}</span>
          </div>
          <div className="add-contact-actions">
            <button
              type="submit"
              form="contact-form"
              className="add-contact-btn-save"
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="btn-spinner" />}
              <span>{isEditing ? "Save changes" : "Save contact"}</span>
            </button>
            <Link to="/contacts" className="add-contact-btn-cancel">
              Cancel
            </Link>
          </div>
        </div>

        <main className="add-contact-content">
          {/* Form title area (Figma 1564:163454 / 1559:162780) */}
          <div className="add-contact-header">
            <h1 className="add-contact-title">{pageTitle}</h1>
            <p className="add-contact-subtitle">{pageSubtitle}</p>
          </div>

          {/* Form with 6 Cards */}
          <form
            id="contact-form"
            className="add-contact-form"
            noValidate
            onSubmit={handleSubmit}
          >
            {/* CARD 1: BUSINESS (Figma 1564:163458) */}
            <section className="add-contact-card">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Business</h2>
                <p className="add-contact-card-desc">
                  Who you’re billing or paying
                </p>
              </div>

              <div className="add-contact-card-fields">
                <div className="add-contact-field-row">
                  {/* Contact Type */}
                  <div className="add-contact-field-group add-contact-select-field w-260">
                    <label className="add-contact-label" htmlFor="contactType">
                      Contact type
                    </label>
                    <ContactSelect
                      id="contactType"
                      value={values.contactType}
                      onChange={handleInputChange("contactType")}
                      ariaLabel="Contact type"
                      options={[
                        { value: "Customer", label: "Customer" },
                        { value: "Supplier", label: "Supplier" },
                      ]}
                    />
                  </div>

                  {/* Status */}
                  <div className="add-contact-field-group add-contact-select-field w-260">
                    <label
                      className="add-contact-label"
                      htmlFor="contactStatus"
                    >
                      Status
                    </label>
                    <ContactSelect
                      id="contactStatus"
                      value={values.contactStatus}
                      onChange={handleInputChange("contactStatus")}
                      ariaLabel="Status"
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                        { value: "Archived", label: "Archived" },
                      ]}
                    />
                  </div>
                </div>

                {/* Legal Name */}
                <div className="add-contact-field-group w-532">
                  <label className="add-contact-label" htmlFor="companyName">
                    Legal name{" "}
                    <span className="add-contact-required-star">*</span>
                  </label>
                  <input
                    id="companyName"
                    aria-required="true"
                    aria-invalid={Boolean(errors.companyName)}
                    aria-describedby={errors.companyName ? "companyName-error" : undefined}
                    onBlur={() => setErrors((current) => ({ ...current, companyName: contactFieldError("companyName", values.companyName) }))}
                    type="text"
                    className={`add-contact-input ${
                      errors.companyName ? "has-error" : ""
                    }`}
                    placeholder="e.g. Harbour Constructions Pty Ltd"
                    value={values.companyName}
                    onChange={handleInputChange("companyName")}
                  />
                  <button
                    type="button"
                    className="add-contact-btn-cancel add-contact-abr-trigger"
                    disabled={abrBusy || values.companyName.trim().length < 2}
                    onClick={() => runAbrLookup()}
                  >
                    {abrBusy ? "Looking up…" : "Find company in ABR"}
                  </button>
                  {errors.companyName && (
                    <p className="add-contact-error-msg">
                      <span id="companyName-error">{errors.companyName}</span>
                    </p>
                  )}
                </div>

                <fieldset className="add-contact-rcti-settings">
                  <legend>RCTI eligibility settings</legend>
                  <div className="add-contact-rcti-grid">
                    <div className="add-contact-field-group add-contact-select-field">
                      <label
                        className="add-contact-label"
                        htmlFor="gstRegistered"
                      >
                        Registered for GST
                      </label>
                      <ContactSelect
                        id="gstRegistered"
                        value={toConfirmationValue(values.gstRegistered)}
                        onChange={(event) =>
                          setValues((current) => ({
                            ...current,
                            gstRegistered: fromConfirmationValue(
                              event.target.value
                            ),
                          }))
                        }
                        ariaLabel="Registered for GST"
                        options={CONFIRMATION_OPTIONS}
                      />
                    </div>

                    <div className="add-contact-field-group add-contact-select-field">
                      <label
                        className="add-contact-label"
                        htmlFor="isLargeBusiness"
                      >
                        Large business
                      </label>
                      <ContactSelect
                        id="isLargeBusiness"
                        value={toConfirmationValue(values.isLargeBusiness)}
                        onChange={(event) =>
                          setValues((current) => ({
                            ...current,
                            isLargeBusiness: fromConfirmationValue(
                              event.target.value
                            ),
                          }))
                        }
                        ariaLabel="Large business"
                        options={CONFIRMATION_OPTIONS}
                      />
                    </div>

                    <div className="add-contact-field-group add-contact-select-field">
                      <label
                        className="add-contact-label"
                        htmlFor="isGovernmentEntity"
                      >
                        Government entity
                      </label>
                      <ContactSelect
                        id="isGovernmentEntity"
                        value={toConfirmationValue(values.isGovernmentEntity)}
                        onChange={(event) =>
                          setValues((current) => ({
                            ...current,
                            isGovernmentEntity: fromConfirmationValue(
                              event.target.value
                            ),
                          }))
                        }
                        ariaLabel="Government entity"
                        options={CONFIRMATION_OPTIONS}
                      />
                    </div>
                  </div>
                </fieldset>

                {/* ABN */}
                <div className="add-contact-field-group w-532">
                  <label className="add-contact-label" htmlFor="abnNumber">
                    ABN
                  </label>
                  <input
                    id="abnNumber"
                    type="text"
                    className={`add-contact-input ${
                      errors.abnNumber ? "has-error" : ""
                    }`}
                    placeholder="11 digits"
                    value={formatAbn(values.abnNumber)}
                    onChange={handleInputChange("abnNumber")}
                  />
                  <div className="add-contact-abr-actions">
                    <button
                      type="button"
                      className="add-contact-btn-save"
                      disabled={abrBusy || !values.abnNumber.trim()}
                      onClick={() => runAbrLookup(values.abnNumber)}
                    >
                      {abrBusy ? "Checking…" : "Check registration"}
                    </button>
                    <a
                      className="add-contact-abr-link"
                      href="https://abr.business.gov.au/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LuSearch aria-hidden="true" />
                      Search ABN Lookup
                    </a>
                  </div>
                  <div
                    className="add-contact-abr-feedback"
                    aria-live="polite"
                    aria-busy={abrBusy}
                  >
                    {abrMessage && (
                      <p className="add-contact-abr-message">{abrMessage}</p>
                    )}
                    {abrMatches.length > 0 && (
                      <ul className="add-contact-abr-results">
                        {abrMatches.map((business, index) => (
                          <li key={`${business.abn}-${index}`}>
                            <div className="add-contact-abr-business">
                              <strong>{business.companyName}</strong>
                              <span className="add-contact-abr-meta">
                                ABN{" "}
                                {business.abn.replace(
                                  /^(\d{2})(\d{3})(\d{3})(\d{3})$/,
                                  "$1 $2 $3 $4"
                                )}
                                {business.state ? ` · ${business.state}` : ""}
                              </span>
                              {business.verified && (
                                <span
                                  className={`add-contact-abr-status ${
                                    business.active ? "is-active" : ""
                                  }`}
                                >
                                  {business.active
                                    ? "Active registration"
                                    : `${business.status} registration`}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="add-contact-btn-cancel"
                              disabled={
                                abrBusy ||
                                (business.verified && !business.active)
                              }
                              onClick={() =>
                                business.verified
                                  ? applyAbrDetails(business)
                                  : runAbrLookup(business.abn)
                              }
                            >
                              {business.verified
                                ? "Use details"
                                : "Check registration"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.abnNumber && (
                    <p className="add-contact-error-msg">{errors.abnNumber}</p>
                  )}
                </div>
              </div>
            </section>

            {/* CARD 2: PRIMARY PERSON (Figma 1564:163467) */}
            <section className="add-contact-card">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Primary person</h2>
                <p className="add-contact-card-desc">
                  Main contact for this business
                </p>
              </div>

              <div className="add-contact-card-fields">
                <div className="add-contact-field-row">
                  {/* Full Name */}
                  <div className="add-contact-field-group w-260">
                    <label className="add-contact-label" htmlFor="primaryName">
                      Full name
                    </label>
                    <input
                      id="primaryName"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "primaryName-error" : undefined}
                      onBlur={() => setErrors((current) => ({ ...current, name: contactFieldError("name", values.name) }))}
                      type="text"
                      className={`add-contact-input ${
                        errors.name ? "has-error" : ""
                      }`}
                      placeholder="Contact person"
                      value={values.name}
                      onChange={handleInputChange("name")}
                    />
                    {errors.name && (
                      <p id="primaryName-error" className="add-contact-error-msg">{errors.name}</p>
                    )}
                  </div>

                  {/* Mobile */}
                  <div className="add-contact-field-group w-260">
                    <label className="add-contact-label" htmlFor="phoneNumber">
                      Mobile
                    </label>
                    <input
                      id="phoneNumber"
                      type="text"
                      className={`add-contact-input ${
                        errors.phoneNumber ? "has-error" : ""
                      }`}
                      placeholder="+61 412 345 678"
                      value={values.phoneNumber}
                      onChange={handleInputChange("phoneNumber")}
                    />
                    {errors.phoneNumber && (
                      <p className="add-contact-error-msg">
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="add-contact-field-group w-532">
                  <label className="add-contact-label" htmlFor="emailAddress">
                    Email <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="emailAddress"
                    aria-required="true"
                    aria-invalid={Boolean(errors.emailAddress)}
                    aria-describedby={errors.emailAddress ? "emailAddress-error" : undefined}
                    onBlur={() => setErrors((current) => ({ ...current, emailAddress: contactFieldError("emailAddress", values.emailAddress) }))}
                    type="email"
                    className={`add-contact-input ${
                      errors.emailAddress ? "has-error" : ""
                    }`}
                    placeholder="accounts@company.com.au"
                    value={values.emailAddress}
                    onChange={handleInputChange("emailAddress")}
                  />
                  {errors.emailAddress && (
                    <p className="add-contact-error-msg">
                      <span id="emailAddress-error">{errors.emailAddress}</span>
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* CARD 3: BILLING (Figma 1564:163476) */}
            <section className="add-contact-card">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Billing</h2>
                <p className="add-contact-card-desc">Applied to new invoices</p>
              </div>

              <div className="add-contact-card-fields">
                <div className="add-contact-field-row">
                  {/* Payment Terms */}
                  <div className="add-contact-field-group add-contact-select-field w-260">
                    <label className="add-contact-label" htmlFor="paymentTerms">
                      Payment terms
                    </label>
                    <ContactSelect
                      id="paymentTerms"
                      value={values.paymentTerms}
                      onChange={handleInputChange("paymentTerms")}
                      ariaLabel="Payment terms"
                      options={[
                        { value: "7 Days", label: "7 days" },
                        { value: "14 Days", label: "14 days" },
                        { value: "30 Days", label: "30 days" },
                        { value: "Due on Receipt", label: "Due on receipt" },
                        { value: "EndOfMonth", label: "End of month" },
                      ]}
                    />
                  </div>

                  {/* Invoice Reminders Switch Toggle */}
                  <div className="add-contact-field-group add-contact-reminders">
                    <span className="add-contact-label">Invoice reminders</span>
                    <div
                      className="add-contact-toggle-wrap"
                      onClick={handleReminderToggle}
                      role="switch"
                      aria-checked={isReminderOn}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          handleReminderToggle();
                        }
                      }}
                    >
                      <div
                        className={`add-contact-toggle-switch ${
                          isReminderOn ? "is-on" : ""
                        }`}
                      >
                        <div className="add-contact-toggle-thumb" />
                      </div>
                      <span className="add-contact-toggle-label">
                        {isReminderOn ? "Send automatically" : "Reminders off"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CARD 4: ADDRESS (Figma 1564:163487 & 1569:88250) */}
            <section className="add-contact-card">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Address</h2>
                <p className="add-contact-card-desc">Optional</p>
              </div>

              <div className="add-contact-card-fields">
                {/* Single Street Address Input */}
                <div className="add-contact-field-group w-532">
                  <label className="add-contact-label" htmlFor="streetAddress">
                    Street address
                  </label>
                  <input
                    id="streetAddress"
                    type="text"
                    className="add-contact-input"
                    placeholder="Start typing an address"
                    value={values.streetAddress}
                    onChange={handleInputChange("streetAddress")}
                  />
                </div>

                {/* Enter Address Manually Accordion Disclosure */}
                <button
                  type="button"
                  className={`add-contact-accordion-header ${
                    manualAddressExpanded ? "expanded" : ""
                  }`}
                  onClick={() => setManualAddressExpanded((prev) => !prev)}
                >
                  <span>Enter address manually</span>
                  <LuChevronRight />
                </button>

                {/* Expanded Manual Address Panel */}
                {manualAddressExpanded && (
                  <div className="add-contact-manual-address-panel">
                    <div className="add-contact-field-row">
                      <div className="add-contact-field-group flex-1">
                        <label
                          className="add-contact-label"
                          htmlFor="addressLine1"
                        >
                          Address line 1
                        </label>
                        <input
                          id="addressLine1"
                          type="text"
                          className="add-contact-input"
                          placeholder="e.g. 123 Smith Street"
                          value={values.address}
                          onChange={handleInputChange("address")}
                        />
                      </div>
                      <div className="add-contact-field-group flex-1">
                        <label
                          className="add-contact-label"
                          htmlFor="addressLine2"
                        >
                          Address line 2 (optional)
                        </label>
                        <input
                          id="addressLine2"
                          type="text"
                          className="add-contact-input"
                          placeholder="e.g. Unit 2, Level 1"
                          value={values.addressLine2 || ""}
                          onChange={handleInputChange("addressLine2")}
                        />
                      </div>
                    </div>

                    <div className="add-contact-field-row">
                      <div className="add-contact-field-group flex-1">
                        <label className="add-contact-label" htmlFor="city">
                          Suburb
                        </label>
                        <input
                          id="city"
                          type="text"
                          className="add-contact-input"
                          placeholder="e.g. Sydney"
                          value={values.city}
                          onChange={handleInputChange("city")}
                        />
                      </div>

                      <div className="add-contact-field-group add-contact-select-field flex-1">
                        <label className="add-contact-label" htmlFor="stateId">
                          State
                        </label>
                        <ContactSelect
                          id="stateId"
                          value={values.stateId}
                          onChange={handleInputChange("stateId")}
                          ariaLabel="State"
                          options={[
                            { value: "", label: "Select state" },
                            ...stateList.map((st) => ({
                              value: st.key,
                              label: st.value,
                            })),
                          ]}
                        />
                      </div>

                      <div className="add-contact-field-group w-160">
                        <label className="add-contact-label" htmlFor="zipCode">
                          Postcode
                        </label>
                        <input
                          id="zipCode"
                          type="text"
                          className="add-contact-input"
                          placeholder="e.g. 2000"
                          value={values.zipCode}
                          onChange={handleInputChange("zipCode")}
                        />
                      </div>
                    </div>

                    <div className="add-contact-field-row">
                      <div className="add-contact-field-group add-contact-select-field w-260">
                        <label
                          className="add-contact-label"
                          htmlFor="countryId"
                        >
                          Country
                        </label>
                        <ContactSelect
                          id="countryId"
                          value={values.countryId}
                          onChange={handleInputChange("countryId")}
                          ariaLabel="Country"
                          options={[
                            { value: "", label: "Select country" },
                            ...countryList.map((c) => ({
                              value: c.key,
                              label: c.value,
                            })),
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* CARD 5: BANK DETAILS (Figma 1564:163494) */}
            <section className="add-contact-card">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Bank details</h2>
                <p className="add-contact-card-desc">
                  Optional · used for RCTI and refunds
                </p>
              </div>

              <div className="add-contact-card-fields">
                <div className="add-contact-field-row">
                  <div className="add-contact-field-group w-260">
                    <label
                      className="add-contact-label"
                      htmlFor="bankAccountName"
                    >
                      Account name
                    </label>
                    <input
                      id="bankAccountName"
                      type="text"
                      className="add-contact-input"
                      placeholder="Account name"
                      value={values.bankAccountName || ""}
                      onChange={handleInputChange("bankAccountName")}
                    />
                  </div>

                  <div className="add-contact-field-group w-160">
                    <label className="add-contact-label" htmlFor="bankBsbCode">
                      BSB
                    </label>
                    <input
                      id="bankBsbCode"
                      type="text"
                      className="add-contact-input"
                      placeholder="000-000"
                      value={values.bankBsbCode || ""}
                      onChange={handleInputChange("bankBsbCode")}
                    />
                  </div>

                  <div className="add-contact-field-group w-260">
                    <label
                      className="add-contact-label"
                      htmlFor="bankAccountNumber"
                    >
                      Account number
                    </label>
                    <input
                      id="bankAccountNumber"
                      type="text"
                      className="add-contact-input"
                      placeholder="Account number"
                      value={values.bankAccountNumber || ""}
                      onChange={handleInputChange("bankAccountNumber")}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* CARD 6: LOGO & NOTES (Figma 1564:163503) */}
            <section className="add-contact-card add-contact-card--logo">
              <div className="add-contact-card-intro">
                <h2 className="add-contact-card-title">Logo &amp; notes</h2>
                <p className="add-contact-card-desc">Optional</p>
              </div>

              <div className="add-contact-card-fields">
                {/* Logo Row */}
                <div className="add-contact-logo-row">
                  <div className="add-contact-logo-preview">
                    {fileUrl ? (
                      <img src={fileUrl} alt="Contact Logo" />
                    ) : (
                      <>
                        <LuBuilding2 className="add-contact-logo-placeholder-icon" />
                        <span className="add-contact-logo-placeholder-text">
                          No logo
                        </span>
                      </>
                    )}
                  </div>

                  <div className="add-contact-logo-actions">
                    <div className="add-contact-logo-buttons">
                      <label
                        htmlFor="logo-upload"
                        className="add-contact-btn-cancel"
                        style={{ cursor: "pointer" }}
                      >
                        <LuUpload style={{ marginRight: 6 }} />
                        <span>Upload logo</span>
                        <input
                          id="logo-upload"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                        />
                      </label>
                      {fileUrl && (
                        <button
                          type="button"
                          className="add-contact-btn-cancel"
                          onClick={removeLogo}
                          aria-label="Remove logo"
                        >
                          <LuTrash2 />
                        </button>
                      )}
                    </div>
                    <p className="add-contact-logo-hint">
                      PNG or SVG, up to 2 MB
                    </p>
                  </div>
                </div>

                {/* Notes Textarea */}
                <div className="add-contact-field-group w-532">
                  <label className="add-contact-label" htmlFor="contactNotes">
                    Notes
                  </label>
                  <textarea
                    id="contactNotes"
                    className="add-contact-textarea"
                    placeholder="Only visible to your team"
                    value={values.notes || ""}
                    onChange={handleInputChange("notes")}
                  />
                </div>
              </div>
            </section>
          </form>
        </main>
      </div>

      {/* CROP IMAGE MODAL */}
      <Modal open={openCropModal} onClose={() => setOpenCropModal(false)}>
        <div className="contact-modal-overlay">
          <div
            className="contact-modal-dialog"
            style={{ maxWidth: 500, height: 440, position: "relative" }}
          >
            <div className="contact-modal-header">
              <h3 className="contact-modal-title">Crop Logo</h3>
              <button
                type="button"
                className="contact-modal-close"
                onClick={() => setOpenCropModal(false)}
              >
                <LuX />
              </button>
            </div>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: 280,
                backgroundColor: "#333",
              }}
            >
              <Cropper
                image={fileUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className="contact-modal-footer">
              <button
                type="button"
                className="contact-modal-btn-cancel"
                onClick={() => setOpenCropModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="contact-modal-btn-confirm"
                onClick={saveCroppedImage}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* DUPLICATE CONTACT WARNING MODAL (Figma 1591:151462) */}
      {duplicateModalOpen && (
        <div
          className="duplicate-modal-overlay"
          onClick={() => setDuplicateModalOpen(false)}
        >
          <div
            className="duplicate-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="duplicate-modal-header">
              <div className="duplicate-modal-title-wrap">
                <span className="duplicate-modal-icon">
                  <LuTriangleAlert />
                </span>
                <h3 className="duplicate-modal-title">
                  Similar contact already exists
                </h3>
              </div>
              <button
                type="button"
                className="duplicate-modal-close"
                onClick={() => setDuplicateModalOpen(false)}
                aria-label="Close dialog"
              >
                <LuX />
              </button>
            </div>
            <div className="duplicate-modal-divider" />
            <div className="duplicate-modal-body">
              <p>
                {duplicateMessage ||
                  "A contact with a similar name or ABN already exists. Review the existing contact before creating another."}
              </p>
            </div>
            <div className="duplicate-modal-footer">
              <button
                type="button"
                className="contact-modal-btn-cancel"
                onClick={() => setDuplicateModalOpen(false)}
              >
                Cancel
              </button>
              <Link
                to="/contacts"
                className="contact-modal-btn-confirm"
                style={{ textDecoration: "none" }}
              >
                View existing contact
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
